import { useState, useCallback, useMemo } from 'react';

export interface ValidationRule<T> {
  test: (value: T) => boolean;
  message: string;
}

export interface FieldConfig<T> {
  required?: boolean;
  rules?: ValidationRule<T>[];
}

export type FormConfig<T> = {
  [K in keyof T]?: FieldConfig<any>;
};

/**
 * Hook for form validation and state management
 * Replaces repeated form patterns across components
 */
export const useFormValidation = <T extends Record<string, any>>(
  initialValues: T,
  config: Partial<FormConfig<T>> = {}
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate a single field
  const validateField = useCallback((name: keyof T, value: any): string | null => {
    const fieldConfig = config[name];
    if (!fieldConfig) {return null;}

    // Required validation
    if (fieldConfig.required && (!value || value.toString().trim() === '')) {
      return `${String(name)} is required`;
    }

    // Custom rules validation
    if (fieldConfig.rules && value) {
      for (const rule of fieldConfig.rules) {
        if (!rule.test(value)) {
          return rule.message;
        }
      }
    }

    return null;
  }, [config]);

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    Object.keys(values).forEach((key) => {
      const error = validateField(key as keyof T, values[key as keyof T]);
      if (error) {
        newErrors[key as keyof T] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validateField]);

  // Handle field change
  const handleChange = useCallback((name: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing, but preserve login errors that need manual clearing
    if (errors[name] && !errors[name]?.includes('Forgot your password?')) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  // Handle field blur
  const handleBlur = useCallback((name: keyof T) => {
    setTouched(prev => ({ ...prev, [name]: true }));

    // Validate field on blur
    const error = validateField(name, values[name]);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  }, [validateField, values]);

  // Handle form submission
  const handleSubmit = useCallback(async (onSubmit: (values: T) => Promise<void> | void) => {
    setIsSubmitting(true);

    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key as keyof T] = true;
      return acc;
    }, {} as Record<keyof T, boolean>);
    setTouched(allTouched);

    try {
      if (validateForm()) {
        await onSubmit(values);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validateForm]);

  // Reset form
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  // Set field error manually
  const setFieldError = useCallback((name: keyof T, error: string) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  }, []);

  // Get field props for easy binding
  const getFieldProps = useCallback((name: keyof T) => ({
    value: values[name],
    onChange: (value: any) => handleChange(name, value),
    onBlur: () => handleBlur(name),
    error: touched[name] ? errors[name] : undefined,
    name: String(name)
  }), [values, handleChange, handleBlur, touched, errors]);

  // Calculate isValid based on current values, not just absence of errors
  const isValid = useMemo(() => {
    // Check if all required fields have values
    for (const [key, fieldConfig] of Object.entries(config)) {
      const value = values[key as keyof T];
      
      // Required field check
      if (fieldConfig.required && (!value || String(value).trim() === '')) {
        return false;
      }
      
      // Rules validation check
      if (fieldConfig.rules && value) {
        for (const rule of fieldConfig.rules) {
          if (!rule.test(value)) {
            return false;
          }
        }
      }
    }
    return true;
  }, [values, config]);

  const hasErrors = Object.values(errors).some(error => error);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid: isValid && !hasErrors,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    setFieldError,
    getFieldProps,
    validateForm
  };
};

export default useFormValidation;
